import React, { useEffect, useState } from 'react';
import { Field, useFormikContext } from 'formik';

import { FormErrorUI, FormLabelUI, AutoCompleteSelectUI, DatePickerUI, InputUI } from '../Interface';
import employeeAttendenceService from '../../service/employeeAttendence.service';
import { EmployeeAttendanceMode } from '../../config/employeeAttendance';
import useParam from '../../context/ParamContext';
import { generateAutocompleteItems } from '../../utils/helper';

const EmployeeAttendanceFields = ({
  viewField,
  selectedValues,
  selectedRow,
  autocompleteOptions,
  mode,
  autocompleteData,
}) => {
  const { params } = useParam();
  const { values, validateField, setFieldValue, errors } = useFormikContext();
  const [taskOptions, setTaskOptions] = useState(autocompleteOptions.simpleTask);

  useEffect(() => {
    getOrderNumber();
  }, [values]);

  useEffect(() => {
    if (params.SHOW_EMPLOYEE_ATTENDANCE_SIMPLETASK_FIELD !== 'Y') return;

    if (values.projectId) {
      const filteredTasks = autocompleteData?.simpleTask?.filter((item) => item.projectId === values.projectId);

      if (filteredTasks) {
        setTaskOptions(generateAutocompleteItems(filteredTasks, 'simpleTaskName', 'simpleTaskId'));
      } else {
        setTaskOptions([]);
      }
    } else {
      setTaskOptions(autocompleteOptions.simpleTask);
    }
  }, [values.projectId]);

  const getOrderNumber = async () => {
    if (values.projectId) {
      const orderNumber = await employeeAttendenceService.getOrderNumber(values.projectId);
      const sanitizedOrderNumber = typeof orderNumber === 'string' ? orderNumber : '';
      setFieldValue('orderNumber', sanitizedOrderNumber);
    } else {
      setFieldValue('orderNumber', '');
    }
  };

  return (
    <div className="grid grid-cols-5 gap-x-4 gap-y-2 md:mx-4 mb-5">
      <div className="col-span-5 md:col-span-1">
        <Field
          type="date"
          name="date"
          component={DatePickerUI}
          value={values.date}
          fullWidth
          disabled={viewField}
          label={<FormLabelUI text="Dátum" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.date} />}
          onChange={(date) => {
            setFieldValue('date', date).then(() => {
              validateField('date');
            });
          }}
          InputLabelProps={{ shrink: values.date !== '' }}
        />
      </div>
      {params.SHOW_EMPLOYEE_ATTENDANCE_SIMPLETASK_FIELD === 'Y' && (
        <div className="col-span-5 md:col-span-2">
          <Field
            type="autocomplete"
            name="simpleTaskId"
            component={AutoCompleteSelectUI}
            fullWidth
            label={<FormLabelUI text="Feladat" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.simpleTaskId} />}
            onChange={(_e, newVal) => {
              const findedTask = autocompleteData?.simpleTask.find((item) => item.simpleTaskId === newVal?.value);

              if (findedTask) {
                setFieldValue('projectId', findedTask.projectId ?? null);
              } else {
                setFieldValue('projectId', null);
              }

              setFieldValue('simpleTaskId', newVal?.value ?? null).then(() => {
                validateField('simpleTaskId');
              });
            }}
            disabled={viewField || EmployeeAttendanceMode.DIALOG === mode}
            InputLabelProps={{ shrink: values.simpleTaskId !== '' }}
            selectedValue={values.simpleTaskId}
            selectedLabelValue={selectedValues?.simpleTask?.simpleTaskName}
            dataset={taskOptions}
          />
        </div>
      )}
      <div className="col-span-5 md:col-span-2">
        <Field
          type="autocomplete"
          name="projectId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Projekt" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.projectId} />}
          onChange={(_e, newVal) => {
            setFieldValue('projectId', newVal?.value ?? null).then(() => {
              validateField('projectId');
            });
          }}
          disabled={viewField || EmployeeAttendanceMode.DIALOG === mode || values.simpleTaskId}
          InputLabelProps={{ shrink: values.projectId !== '' }}
          selectedValue={values.projectId}
          selectedLabelValue={selectedValues?.project?.projectName}
          dataset={autocompleteOptions.project}
        />
      </div>
      {EmployeeAttendanceMode.MAIN === mode && (
        <>
          <div className="col-span-5 md:col-span-2">
            <Field
              type="text"
              name="orderNumber"
              component={InputUI}
              disabled
              fullWidth
              label={<FormLabelUI text="Rendelési szám" />}
              variant="standard"
              helperText={<FormErrorUI message={errors.orderNumber} />}
              InputLabelProps={{ shrink: values.orderNumber !== '' }}
              value={values.orderNumber}
            />
          </div>
          <div className="col-span-5 md:col-span-2">
            <Field
              type="autocomplete"
              name="statusId"
              component={AutoCompleteSelectUI}
              fullWidth
              label={<FormLabelUI text="Státusz" />}
              variant="standard"
              helperText={<FormErrorUI message={errors.statusId} />}
              onChange={(_e, newVal) => {
                setFieldValue('statusId', newVal?.value ?? null).then(() => {
                  validateField('statusId');
                });
              }}
              disabled={viewField && selectedRow.id.length === 0}
              InputLabelProps={{ shrink: values.statusId !== '' }}
              selectedValue={values.statusId}
              selectedLabelValue={selectedValues?.status?.itemName}
              dataset={autocompleteOptions.status}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default EmployeeAttendanceFields;
